import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { startOutboundCall } from '@/domain/callFlow';
import { customerName, relativeFromIso, simTimeShort } from '@/utils/format';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import type { QueueItem, QueueKind, QueueChannel } from '@/domain/queueEngine';
import { queueActionsFor } from '@/domain/queueEngine';
import TopupChaseBar from '@/components/queue/TopupChaseBar';
import type { Channel } from '@/domain/types';
import {
  Phone,
  PhoneIncoming,
  Wallet,
  Sparkles,
  MessageSquare,
  Mail,
  AtSign,
  ScrollText,
  AlarmClock,
  ChevronDown,
  ChevronUp,
  RotateCcw,
} from 'lucide-react';
import clsx from 'clsx';

const KIND_META: Record<
  QueueKind,
  { icon: typeof Phone; bandLabel: string; tone: 'critical' | 'warn' | 'cool' }
> = {
  'deposit-on-file': { icon: Wallet, bandLabel: 'DEPOSIT ON FILE', tone: 'warn' },
  'callback-firing': { icon: AlarmClock, bandLabel: 'NOW', tone: 'critical' },
  'callback-today': { icon: AlarmClock, bandLabel: 'TODAY', tone: 'warn' },
  'pending-deposit': { icon: Wallet, bandLabel: 'DEPOSIT', tone: 'warn' },
  'missed-call': { icon: PhoneIncoming, bandLabel: 'MISSED', tone: 'warn' },
  'unread-message': { icon: MessageSquare, bandLabel: 'UNREAD', tone: 'cool' },
  'pushed-lead': { icon: MessageSquare, bandLabel: 'MESSAGE LEAD', tone: 'cool' },
  'missed-topup': { icon: Wallet, bandLabel: 'TOP-UP MISSED', tone: 'warn' },
  'unfinished-piece': { icon: ScrollText, bandLabel: 'REBOOK', tone: 'cool' },
  'rebook-followup': { icon: RotateCcw, bandLabel: 'REBOOK FOLLOW-UP', tone: 'warn' },
  'healing-check': { icon: MessageSquare, bandLabel: 'HEALING CHECK', tone: 'cool' },
  'gap-candidate': { icon: Sparkles, bandLabel: 'GAP FILL', tone: 'cool' },
};

// A pushed card is labelled by where it came from (B8 / 75nf), not by whichever
// signal also fired.
const ORIGIN_LABEL: Record<NonNullable<QueueItem['origin']>, string> = {
  'hot-lead': 'HOT LEAD',
  'date-taken': 'DATE TAKEN',
  'self-book-expired': 'SELF-BOOK EXPIRED',
  'web-enquiry': 'WEB ENQUIRY',
};

function bandLabelOf(item: QueueItem): string {
  if (item.origin && (item.kind === 'pushed-lead' || item.kind === 'callback-firing')) {
    return item.kind === 'callback-firing' ? `CALLBACK · ${ORIGIN_LABEL[item.origin]}` : ORIGIN_LABEL[item.origin];
  }
  return KIND_META[item.kind].bandLabel;
}

const CHANNEL_META: Record<
  QueueChannel,
  { label: string; icon: typeof Phone }
> = {
  voice: { label: 'Call', icon: Phone },
  sms: { label: 'Text', icon: MessageSquare },
  'meta-dm': { label: 'Messenger', icon: MessageSquare },
  'ig-dm': { label: 'IG DM', icon: AtSign },
  email: { label: 'Email', icon: Mail },
};

const TONE_CLASS = {
  critical: 'border-blood/60 bg-blood/5',
  warn: 'border-gold-bright/50 bg-gold/5',
  cool: 'border-gold-dim/30 bg-ink-deep',
};

const BADGE_TONE = {
  critical: 'bg-blood/20 text-blood',
  warn: 'bg-gold/20 text-gold-bright',
  cool: 'bg-cream-soft/10 text-cream-soft',
};

// Thin left accent per cohort lane, distinct from TONE_CLASS (which owns the
// card's full border/background). No accent for cohort-less rail kinds.
const COHORT_ACCENT: Record<NonNullable<QueueItem['cohort']>, string> = {
  fresh: 'border-l-2 border-l-azure',
  money: 'border-l-2 border-l-mint',
  'open-business': 'border-l-2 border-l-gold',
};

// Short channel label for the last-message preview. Covers every Channel value,
// including the non-contact ones (web-form, walk-in) a conversation can carry.
const CHANNEL_SHORT: Record<Channel, string> = {
  'meta-dm': 'Messenger',
  'ig-dm': 'IG',
  sms: 'SMS',
  voice: 'Call',
  'web-form': 'Web',
  'walk-in': 'Walk-in',
  email: 'Email',
};

interface Props {
  items: QueueItem[];
  emptyState?: React.ReactNode;
  /**
   * Opens the customer's workspace modal for this item, optionally on a given
   * channel. When omitted, contact buttons and the body click fall back to
   * navigating to the customer page so the card still works standalone.
   */
  onOpen?: (item: QueueItem, opts?: { channel?: QueueChannel }) => void;
  /** Flag the first (non-dormant) card as "Up next" — the obvious next action. */
  upNext?: boolean;
}

export default function QueueDialer({ items, emptyState, onOpen, upNext }: Props) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const resolveRebookFollowup = useBrainStore((s) => s.resolveRebookFollowup);
  // Subscribed for reactivity; the per-item check below mirrors
  // activeDeclineFlagForCustomer (an active flag = present + unresolved).
  const declineFlags = useBrainStore((s) => s.noDepositDeclineFlags);
  const agents = useBrainStore((s) => s.agents);
  const sessionAgentId = useSessionStore((s) => s.agentId);
  const clockIso = useSimulationStore((s) => s.clockIso);

  if (items.length === 0) {
    return (
      <div
        data-testid="queue-empty"
        className="panel-dark p-6 text-center font-serif text-cream-soft"
      >
        {emptyState ?? 'Queue is clear. Pick a gap on the right to start dialling.'}
      </div>
    );
  }

  // A contact button opens the handoff modal on its channel (starting the call
  // first for voice). Without onOpen we keep the standalone navigate behaviour.
  const openItem = (item: QueueItem, channel?: QueueChannel) => {
    if (item.dormant) return;
    if (onOpen) onOpen(item, channel ? { channel } : undefined);
    else navigate(`/customer/${item.customer.id}`);
  };

  const fireContact = (item: QueueItem, channel: QueueChannel) => {
    if (item.dormant) return;
    if (channel === 'voice') {
      startOutboundCall({
        customerId: item.customer.id,
        studioId: item.customer.primaryStudioId,
      });
    }
    openItem(item, channel);
  };

  return (
    <ol className="space-y-2" data-testid="queue-list">
      {items.map((item, index) => {
        const isUpNext = upNext === true && index === 0 && !item.dormant;
        const meta = KIND_META[item.kind];
        const Icon = meta.icon;
        const ch = CHANNEL_META[item.recommendedChannel];
        const ChIcon = ch.icon;
        const channels = queueActionsFor(item);
        const lastMsg =
          item.customer.conversation[item.customer.conversation.length - 1];
        const showWhy = expanded[item.id] ?? false;
        const hasEvidence = (item.evidence?.length ?? 0) > 0;
        const declineFlag = declineFlags[`ndf-${item.customer.id}`];
        const declined = declineFlag != null && declineFlag.resolvedAt == null;
        return (
          <li key={item.id} data-testid={`queue-item-${item.customer.id}`}>
            <article
              onClick={() => openItem(item)}
              role="button"
              tabIndex={item.dormant ? -1 : 0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openItem(item);
                }
              }}
              aria-disabled={item.dormant || undefined}
              data-testid={`queue-card-${item.customer.id}`}
              className={clsx(
                'rounded-lg border px-4 py-3 transition',
                item.dormant ? 'cursor-default' : 'cursor-pointer hover:border-gold-bright/60',
                isUpNext ? 'border-gold-bright ring-1 ring-gold-bright/40 bg-gold/[0.07]' : TONE_CLASS[meta.tone],
                item.cohort ? COHORT_ACCENT[item.cohort] : undefined,
                item.dormant && 'opacity-60',
              )}
            >
            <div className="flex items-start gap-3">
              <div
                className={clsx(
                  'shrink-0 mt-0.5 w-9 h-9 rounded-full flex items-center justify-center',
                  BADGE_TONE[meta.tone],
                )}
              >
                <Icon size={16} />
              </div>
              <div
                className="flex-1 min-w-0 text-left"
                data-testid={`queue-open-${item.customer.id}`}
              >
                <header className="flex items-center gap-2 flex-wrap">
                  {isUpNext ? (
                    <span
                      className="font-inter text-[9px] uppercase tracking-widest font-semibold text-gold-bright"
                      data-testid="queue-upnext-flag"
                    >
                      Up next
                    </span>
                  ) : null}
                  <h3
                    className="font-inter text-base text-cream truncate"
                    data-testid={`queue-item-name-${item.customer.id}`}
                  >
                    {customerName(item.customer)}
                  </h3>
                  <span
                    className={clsx(
                      'px-1.5 py-0.5 rounded-sm font-inter text-[9px] uppercase tracking-widest',
                      BADGE_TONE[meta.tone],
                    )}
                    data-testid={`queue-band-${item.customer.id}`}
                  >
                    {bandLabelOf(item)}
                  </span>
                  {item.matchScore !== undefined ? (
                    <span className="font-inter text-[10px] text-mint uppercase tracking-widest">
                      {item.matchScore}% match
                    </span>
                  ) : null}
                  {declined ? (
                    <span
                      className="px-1.5 py-0.5 rounded-sm bg-blood/20 text-blood font-inter text-[9px] uppercase tracking-widest"
                      data-testid={`queue-decline-badge-${item.customer.id}`}
                      title="Ops declined a no-deposit booking for this customer. Collect a deposit."
                    >
                      No-deposit declined · collect a deposit
                    </span>
                  ) : null}
                  {item.dormant && item.sequenceDueAtIso ? (
                    <span
                      className="px-1.5 py-0.5 rounded-sm border border-dashed border-gold-dim font-inter text-[9px] uppercase tracking-widest text-cream-faint"
                      data-testid={`queue-dormant-${item.customer.id}`}
                    >
                      due {relativeFromIso(item.sequenceDueAtIso)}
                    </span>
                  ) : null}
                </header>
                <p className="font-serif text-sm text-cream-soft mt-1 line-clamp-2">
                  {item.reason}
                  {item.detail ? <span className="text-cream-faint"> · {item.detail}</span> : null}
                </p>
                <p
                  className="font-inter text-[10px] uppercase mt-1.5 tracking-widest text-gold-dim flex items-center gap-1.5 flex-wrap"
                  data-testid={`queue-channel-reason-${item.customer.id}`}
                >
                  <ChIcon size={10} className="text-gold-bright" />
                  <span className="text-gold-bright">{ch.label}</span>
                  <span className="text-cream-faint">·</span>
                  <span className="text-cream-soft normal-case tracking-normal text-[11px] font-serif">
                    {item.channelReason}
                  </span>
                  {item.recommendedAt ? (
                    <>
                      <span className="text-cream-faint">·</span>
                      <span className="text-gold">{simTimeShort(item.recommendedAt)}</span>
                      <span className="text-cream-faint">{relativeFromIso(item.recommendedAt)}</span>
                    </>
                  ) : null}
                </p>
                {item.kind === 'deposit-on-file' && item.ownerAgentId ? (
                  <p
                    className="font-inter text-[10px] uppercase tracking-widest text-gold-dim mt-1"
                    data-testid={`queue-owner-agent-${item.customer.id}`}
                  >
                    {sessionAgentId === item.ownerAgentId
                      ? 'Your re-offer'
                      : `Booked by ${agents[item.ownerAgentId]?.firstName ?? 'agent'}`}
                  </p>
                ) : null}
                {lastMsg ? (
                  <p
                    className="font-serif text-[11px] text-cream-faint mt-1 truncate"
                    data-testid={`queue-last-msg-${item.customer.id}`}
                  >
                    <span className="font-inter uppercase tracking-widest text-[9px] text-gold-dim mr-1.5">
                      {CHANNEL_SHORT[lastMsg.channel]}
                    </span>
                    {lastMsg.body}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                {channels.map((channel, i) => {
                  const cm = CHANNEL_META[channel];
                  const CmIcon = cm.icon;
                  return (
                    <button
                      key={channel}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fireContact(item, channel);
                      }}
                      disabled={item.dormant}
                      data-contact-channel={channel}
                      data-testid={
                        i === 0
                          ? `queue-action-${item.customer.id}`
                          : `queue-action-${channel}-${item.customer.id}`
                      }
                      className={clsx('text-xs', i === 0 ? 'btn-primary' : 'btn-quiet')}
                    >
                      <CmIcon size={12} /> {cm.label}
                    </button>
                  );
                })}
                <span className="font-inter text-[10px] text-cream-faint uppercase tracking-widest">
                  score {item.customer.leadScore}
                </span>
              </div>
            </div>

            {item.kind === 'rebook-followup' && item.rebookFollowupTaskId ? (
              <div
                className="mt-3 border-t border-gold-dim/15 pt-2 flex flex-wrap items-center gap-2"
                data-testid={`queue-rebook-resolve-${item.customer.id}`}
              >
                <span className="font-inter text-[10px] uppercase tracking-widest text-gold-dim mr-1">
                  Resolve
                </span>
                {(
                  [
                    { outcome: 'booked', label: 'Booked', tone: 'mint' },
                    { outcome: 'declined', label: 'Declined', tone: 'blood' },
                    { outcome: 'noContact', label: 'No contact', tone: 'cream' },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.outcome}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      resolveRebookFollowup(item.rebookFollowupTaskId!, {
                        outcome: opt.outcome,
                        resolvedAt: clockIso,
                      });
                    }}
                    data-testid={`queue-rebook-resolve-${opt.outcome}-${item.customer.id}`}
                    className={clsx(
                      'rounded-md border px-2 py-1 text-[11px] uppercase tracking-widest font-inter transition',
                      opt.tone === 'mint' && 'border-mint/40 text-mint hover:bg-mint/10',
                      opt.tone === 'blood' && 'border-blood/40 text-blood hover:bg-blood/10',
                      opt.tone === 'cream' && 'border-gold-dim/40 text-cream-soft hover:bg-cream-soft/10',
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : null}

            {item.topupPlanId ? (
              <div className="mt-3 border-t border-gold-dim/15 pt-2">
                <TopupChaseBar
                  planId={item.topupPlanId}
                  customerId={item.customer.id}
                  testid={`queue-topup-chase-${item.customer.id}`}
                  buttonTestid={(key) => `queue-topup-chase-${key}-${item.customer.id}`}
                />
              </div>
            ) : null}

            {hasEvidence ? (
              <div className="mt-3 border-t border-gold-dim/15 pt-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => ({ ...prev, [item.id]: !showWhy }));
                  }}
                  className="flex items-center gap-1.5 font-inter text-[10px] uppercase tracking-widest text-cream-soft hover:text-gold-bright transition"
                  data-testid={`queue-why-${item.customer.id}`}
                >
                  {showWhy ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                  {showWhy
                    ? 'Hide reasoning'
                    : item.kind === 'gap-candidate'
                      ? 'Why this match?'
                      : 'Why this matters'}
                </button>
                {showWhy ? (
                  <ul
                    className="mt-2 space-y-1"
                    data-testid={`queue-evidence-${item.customer.id}`}
                  >
                    {item.evidence!.map((e, i) => (
                      <li key={i} className="flex items-baseline gap-2 text-[11px]">
                        <span
                          className={clsx(
                            'font-mono shrink-0 w-9 text-right',
                            e.weight > 0 ? 'text-mint' : 'text-blood',
                          )}
                        >
                          {e.weight > 0 ? '+' : ''}
                          {e.weight}
                        </span>
                        <span className="font-serif text-cream-soft leading-snug">
                          {e.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            </article>
          </li>
        );
      })}
    </ol>
  );
}
